// src/features/media/components/VideoDetailPage.tsx

import { useParams } from '@tanstack/react-router';
import { Stack, Grid, Loader, Alert, Text, Tabs } from '@mantine/core';
import { IconAlertCircle, IconFileDescription, IconSubtask, IconPhoto } from '@tabler/icons-react';
import { bcLong } from '@lib2/model/util/string/BaseConverter';
import { useVideo } from '../useVideo';
import { VideoHeader } from './VideoHeader';
import { VideoPreview } from './VideoPreview';
import { VideoMetadataCard } from './VideoMetadataCard';
import { OverviewTab } from './OverviewTab';
import { CaptionsTab } from './CaptionsTab';
import { ImagesTab } from './ImagesTab';

export function VideoDetailPage() {
  const { id: displayId } = useParams({ strict: false }) as { id: string };
  const mediaId = bcLong.getNum(displayId);

  const { data: video, isLoading, isError, error } = useVideo(mediaId);

  if (isLoading) {
    return (
      <Stack align="center" justify="center" h={400}>
        <Loader size="lg" />
        <Text c="dimmed">Loading video...</Text>
      </Stack>
    );
  }

  if (isError || !video) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
        {error?.message ?? 'Failed to load video'}
      </Alert>
    );
  }

  return (
    <Stack gap="lg">
      <VideoHeader video={video} displayId={displayId} />

      <Grid>
        <Grid.Col span={{ base: 12, md: 8 }}>
          <VideoPreview video={video} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <VideoMetadataCard video={video} />
        </Grid.Col>
      </Grid>

      <Tabs defaultValue="overview" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconFileDescription size={16} />}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab value="captions" leftSection={<IconSubtask size={16} />}>
            Captions
          </Tabs.Tab>
          <Tabs.Tab value="images" leftSection={<IconPhoto size={16} />}>
            Images
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview" pt="md" keepMounted>
          <OverviewTab video={video} />
        </Tabs.Panel>

        <Tabs.Panel value="captions" pt="md">
          <CaptionsTab video={video} />
        </Tabs.Panel>

        <Tabs.Panel value="images" pt="md" keepMounted>
          <ImagesTab video={video} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
